import type {
  FiberSelectedPayloadTransformed,
  SelectedElementDesignationTransformed,
} from "schemas/generated/fileModifiers";

import React, { createContext } from "react";

import { useRequiredContext } from "replo-utils/react/context";
import { useShallowStore } from "replo-utils/react/zustand";
import { createStore } from "zustand";

type DisplayMode = "interactive" | "select";

interface ComputedElementDimensions {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ModifiersStoreData {
  selectedElementDesignations: SelectedElementDesignationTransformed[] | null;
  classNames: string;
  displayMode: DisplayMode;
  iframeContainer: HTMLDivElement | null;
  isIframeLoaded: boolean;
  isDeleting: boolean;
  computedElementDimensions: ComputedElementDimensions | null;
  actions: {
    setSelectedElementDesignations: (
      payload: FiberSelectedPayloadTransformed | null,
    ) => void;
    setClassNames: (classNames: string) => void;
    setDisplayMode: (displayMode: DisplayMode) => void;
    resetToDefaults: () => void;
    setIframeContainer: (element: HTMLDivElement | null) => void;
    setIframeLoaded: (loaded: boolean) => void;
    setIsDeleting: (isDeleting: boolean) => void;
    setComputedElementDimensions: (
      dimensions: ComputedElementDimensions | null,
    ) => void;
  };
}

const createModifiersStore = () => {
  return createStore<ModifiersStoreData>((set) => ({
    selectedElementDesignations: null,
    classNames: "",
    displayMode: "select",
    iframeContainer: null,
    isIframeLoaded: false,
    isDeleting: false,
    computedElementDimensions: null,
    actions: {
      setSelectedElementDesignations: (
        payload: FiberSelectedPayloadTransformed | null,
      ) => {
        // When setting a selected element, also derive the related values
        if (payload) {
          set({
            selectedElementDesignations: payload.selectedElementDesignations,
          });
        } else {
          set({
            selectedElementDesignations: null,
            classNames: "",
          });
        }
      },
      setClassNames: (classNames: string) => set({ classNames }),
      setDisplayMode: (displayMode: DisplayMode) => set({ displayMode }),
      resetToDefaults: () => {
        return set({
          selectedElementDesignations: null,
          classNames: "",
          isIframeLoaded: false,
          computedElementDimensions: null,
        });
      },
      setIframeContainer: (element: HTMLDivElement | null) => {
        return set({ iframeContainer: element });
      },
      setIframeLoaded: (loaded: boolean) => set({ isIframeLoaded: loaded }),
      setIsDeleting: (isDeleting: boolean) => set({ isDeleting }),
      setComputedElementDimensions: (
        dimensions: ComputedElementDimensions | null,
      ) => set({ computedElementDimensions: dimensions }),
    },
  }));
};

type ModifiersContextType = {
  store: ReturnType<typeof createModifiersStore>;
};

const ModifiersContext = createContext<ModifiersContextType | null>(null);

export const useModifiersContext = () => {
  return useRequiredContext(ModifiersContext);
};

type ModifiersProviderProps = React.PropsWithChildren;

export function ModifiersProvider({ children }: ModifiersProviderProps) {
  const [store] = React.useState(() => createModifiersStore());

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  return (
    <ModifiersContext.Provider value={contextValue}>
      {children}
    </ModifiersContext.Provider>
  );
}

// NOTE (Kurt, 2025-07-16): Main hook to access the store, only use this if you are sure you
// need to access the entire store. For most cases, use the convenience hooks below.
export function useModifiersStore<T>(
  selector: (state: ModifiersStoreData) => T,
): T {
  const { store } = useModifiersContext();
  return useShallowStore(store, selector);
}

// NOTE (Kurt, 2025-07-16): Convenience hooks for accessing specific values from the store.
// Define more as needed.
export const useSelectedElementDesignations = () => {
  return useModifiersStore((state) => state.selectedElementDesignations);
};

export const useDefaultSelectedElementDesignation = () => {
  return useModifiersStore((state) => {
    return state.selectedElementDesignations?.find(
      (designation) => designation.type === "default",
    );
  });
};

export const useIsAnyElementSelected = () => {
  return useModifiersStore((state) => {
    return Boolean(state.selectedElementDesignations);
  });
};

export const useModifiersStoreActions = () => {
  return useModifiersStore((state) => state.actions);
};

export const useDisplayMode = () => {
  return useModifiersStore((state) => state.displayMode);
};

export const useIsIframeLoaded = () => {
  return useModifiersStore((state) => state.isIframeLoaded);
};

export const useIsDeleting = () => {
  return useModifiersStore((state) => state.isDeleting);
};

export const useComputedElementDimensions = () => {
  return useModifiersStore((state) => state.computedElementDimensions);
};
